import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import type { BillingCycle } from '@features/onboarding/hooks/use-plans';
import { useCancelSubscription } from '../../hooks/useCancelSubscription';
import { useChangePlan } from '../../hooks/useChangePlan';
import { useChangePlanPreview } from '../../hooks/useChangePlanPreview';
import { PlanFlowShell } from './PlanFlowShell';
import { PlanFlowBody, type PlanFlowBodyProps, type PlanFlowView } from './PlanFlowBody';
import { usePlanFlowCard } from './usePlanFlowCard';
import { type ReviewPlanOption } from './PlanFlowReview';
import { useSeatAwarePlanChange } from '../../hooks/useSeatAwarePlanChange';
import {
  linesForChange,
  needsSeatAwareChange,
  previewFacts,
  totalSeats,
  type SubscriptionLine,
} from './plan-flow.items';
import {
  chargesToday,
  copyKeysFor,
  isConfirmDisabled,
  isTrialChange,
  kindOf,
  needsPreview,
  tierBelow,
  todayCharge,
  type PlanFlowMode,
  type PlanFlowStep,
} from './plan-flow.model';

/** A plan as the flow needs it, both cycles resolved. */
export interface PlanFlowOption extends ReviewPlanOption {
  /** Catalogue id to send for the selected cycle. */
  planIdFor: (cycle: BillingCycle) => string;
  /** Price in cents for the selected cycle. */
  priceFor: (cycle: BillingCycle) => number;
}

interface Props {
  agencyId: string;
  opened: boolean;
  mode: PlanFlowMode;
  options: PlanFlowOption[];
  currentKey: string;
  currentCycle: BillingCycle;
  renewalDate: string;
  yearlyDiscountPercent?: number;
  card: { brand: string; lastFour: string } | null;
  /** What the next renewal takes today, for the note under the card form. */
  nextChargeCents?: number;
  initialTarget?: string;
  initialCycle?: BillingCycle;
  /**
   * The lines the subscription holds today.
   *
   * Without them this flow prices one seat and sends one plan id, which is
   * the solo broker's shape; an agency's bill is its seats.
   */
  items?: SubscriptionLine[];
  /** The subscription is in its free period: changes apply now and charge nothing today. */
  trialing?: boolean;
  onClose: () => void;
  /** Told what happened, in words the tab can show as a toast. */
  onChanged: (message: string) => void;
}

/**
 * One modal for changing plan, changing card and cancelling.
 *
 * What used to be a stack — compare, then confirm; summary, then form — is
 * modes and steps inside this component. The buyer never sees a dialog open
 * over another dialog, and switching the target plan recalculates in place.
 *
 * The mode decides the body, not only the words around it: 'card' opens
 * Payien's form and 'cancel' opens the acknowledgements. Both used to render
 * the plan selector under a title that promised something else.
 *
 * @param props - Component props.
 * @returns The plan flow modal.
 */
export function PlanFlowModal(props: Props) {
  const {
    agencyId, opened, mode, options, currentKey, currentCycle, renewalDate,
    yearlyDiscountPercent = 16, card, nextChargeCents = 0, initialTarget,
    initialCycle, items, trialing, onClose, onChanged,
  } = props;
  const { t } = useTranslation('features/pricing');

  // The mode the modal is actually serving: the cancellation offers a
  // downgrade and a switch to yearly, and taking one turns this into the
  // plan flow rather than opening a second dialog over it.
  const [activeMode, setActiveMode] = useState<PlanFlowMode>(mode);
  const [target, setTarget] = useState(initialTarget ?? currentKey);
  const [cycle, setCycle] = useState<BillingCycle>(initialCycle ?? currentCycle);
  const [step, setStep] = useState<PlanFlowStep>('review');
  const [checks, setChecks] = useState<boolean[]>([false, false, false]);
  const [cardUpdated, setCardUpdated] = useState(false);

  const {
    fetchPreview, preview, isLoading: previewLoading, isError: planPreviewFailed,
    refusal: planRefusal,
  } = useChangePlanPreview(agencyId);
  const { changePlan, isPending, isError: changeFailed, reset: resetChange } =
    useChangePlan(agencyId);

  /* A team is billed through its lines; one seat is the plan on its own. */
  const seats = totalSeats(items);
  const seatAware = needsSeatAwareChange(items);
  /*
   * Payien moves the cycle of a team only inside the free period: `change-items`
   * refuses lines on another cycle and `change-plan` refuses a team (BUG-043).
   * Once it is paid there is no way to ask, so the cycle is not offered.
   */
  const cycleLocked = seatAware && !trialing;
  const seatChange = useSeatAwarePlanChange(agencyId);

  /** Resolves a plan id the subscription holds into the chosen cycle. */
  const planIdInCycle = useCallback((planId: string, forCycle: BillingCycle) => {
    const owner = options.find((option) => (
      option.planIdFor('monthly') === planId || option.planIdFor('yearly') === planId
    ));
    return owner ? owner.planIdFor(forCycle) : null;
  }, [options]);

  const view: PlanFlowView = useMemo(() => {
    if (activeMode === 'card' || step === 'card') return 'card';
    return activeMode === 'cancel' ? 'cancel' : 'review';
  }, [activeMode, step]);

  const cardSession = usePlanFlowCard(agencyId, opened && view === 'card');
  const { cancel, isCancelling } = useCancelSubscription(agencyId, () => {
    onChanged(t('planFlow.toast.cancel'));
    onClose();
  });

  // Reopening with a different entry point has to start from that entry
  // point, not from wherever the last visit left the selector.
  const { reset: resetCard } = cardSession;
  useEffect(() => {
    if (!opened) return;
    setActiveMode(mode);
    setTarget(initialTarget ?? currentKey);
    setCycle(initialCycle ?? currentCycle);
    setStep('review');
    setChecks([false, false, false]);
    setCardUpdated(false);
    resetChange();
    resetCard();
  }, [
    opened, mode, initialTarget, initialCycle, currentKey, currentCycle,
    resetChange, resetCard,
  ]);

  const kind = useMemo(
    () => kindOf({ planKey: currentKey, cycle: currentCycle }, { planKey: target, cycle }),
    [currentKey, currentCycle, target, cycle],
  );

  const targetOption = options.find((o) => o.key === target);

  /** The lines this change asks the subscription to hold. */
  const changeLines = useCallback(
    () => linesForChange(items, cycle, targetOption?.planIdFor(cycle) ?? null, planIdInCycle),
    [items, cycle, targetOption, planIdInCycle],
  );

  /** The request body both the preview and the commit are built from. */
  const requestFor = useCallback(() => {
    if (!targetOption) return null;
    return {
      plan_id: targetOption.planIdFor(cycle),
      price_cents: targetOption.priceFor(cycle),
      billing_frequency: cycle,
      billing_cycle_days: cycle === 'yearly' ? 365 : 30,
    };
  }, [targetOption, cycle]);

  // Only a change that charges needs a pro-rata; a scheduled one costs
  // nothing today and asking would put a number on screen that is not owed.
  // In the free period every change is asked: the answer carries the
  // conversion figure, and is where a pending first payment is refused.
  const { fetchPreview: fetchSeatPreview } = seatChange;
  useEffect(() => {
    if (!opened || activeMode !== 'plan' || !needsPreview(kind, trialing)) return;
    if (seatAware) {
      const lines = changeLines();
      if (lines) void fetchSeatPreview(lines);
      return;
    }
    const body = requestFor();
    if (body) void fetchPreview(body);
  }, [opened, activeMode, kind, trialing, seatAware, changeLines, fetchSeatPreview, requestFor, fetchPreview]);

  const facts = previewFacts(
    seatAware,
    seatChange.preview
      ? { ...seatChange.preview, failed: seatChange.previewFailed }
      : { failed: seatChange.previewFailed },
    preview ? { ...preview, failed: planPreviewFailed } : { failed: planPreviewFailed },
  );
  const previewRefusal = seatAware ? seatChange.refusal : planRefusal;
  // Free period: the figure the screen shows is the server's, so until it
  // arrives (or is refused, or fails) the change is not ready to confirm.
  const awaitingTrialFigure = isTrialChange(trialing, kind)
    && facts.nextChargeCents === null && !facts.failed && previewRefusal === null;
  const amountToday = todayCharge(kind, facts.proRataCents);
  const copy = copyKeysFor(activeMode, kind, trialing);
  const busy = isPending || seatChange.isPending || isCancelling || step === 'processing';

  const confirmLabel = busy
    ? t('planFlow.confirm.processing')
    : t(view === 'card' ? 'planFlow.confirm.card' : copy.confirm, {
      amount: amountToday === null ? '—' : formatCents(amountToday),
      plan: targetOption?.title ?? '',
    });

  const handleConfirmPlan = async () => {
    const body = requestFor();
    if (!body || !targetOption) return;
    setStep('processing');
    const lines = seatAware ? changeLines() : null;
    const accepted = lines
      ? await seatChange.apply(lines)
      : await changePlan({
        ...body,
        name: targetOption.title,
        charge_upgrade_difference: chargesToday(kind),
      });
    setStep('review');
    // A refused change used to close the modal and refresh the tab, which
    // reads as success: the buyer went back to a screen showing the plan
    // they still have. Stay open and say so instead.
    if (!accepted) return;
    onChanged(t(copy.toast, { plan: targetOption.title }));
    onClose();
  };

  const handleConfirm = () => {
    // The framed form owns the card data; the footer only asks it to submit,
    // and the answer comes back as a message, not as a promise.
    if (view === 'card') return cardSession.submit();
    if (activeMode === 'cancel') return void cancel();
    return void handleConfirmPlan();
  };

  /** The card was replaced: end the flow, or carry on with the upgrade. */
  const handleCardSaved = () => {
    if (activeMode === 'card') {
      onChanged(t('planFlow.done.card.title'));
      onClose();
      return;
    }
    setCardUpdated(true);
    setStep('review');
  };

  /** Leave the card form: back to the review mid-upgrade, out otherwise. */
  const leaveCardStep = () => {
    if (activeMode === 'card') return onClose();
    return setStep('review');
  };

  /** Take one of the ways out of the cancellation into the plan flow. */
  const switchToPlan = (nextTarget: string, nextCycle: BillingCycle) => {
    setTarget(nextTarget);
    setCycle(nextCycle);
    setActiveMode('plan');
  };

  const downgrade = tierBelow(options, currentKey);

  const body: PlanFlowBodyProps = bodyFor({
    view,
    card: {
      mode: activeMode === 'card' ? 'card' : 'plan',
      formUrl: cardSession.formUrl,
      card,
      chargeTodayCents: activeMode === 'card' ? 0 : (amountToday ?? 0),
      nextChargeCents,
      renewalDate,
      daysRemaining: preview?.days_remaining ?? null,
      newPlanPrice: formatCents(targetOption?.priceFor(cycle) ?? 0),
      submitSignal: cardSession.submitSignal,
      onStateChange: cardSession.onStateChange,
      onSuccess: handleCardSaved,
      onCancel: leaveCardStep,
      onExpired: cardSession.reset,
      onUseCurrent: activeMode === 'plan' ? () => setStep('review') : undefined,
    },
    cancel: {
      renewalDate,
      downgradeTo: downgrade && {
        key: downgrade.key,
        title: downgrade.title,
        priceLabel: t('planFlow.review.perMonth', {
          price: formatCents(downgrade.monthlyEquivalentCents),
        }),
      },
      yearlyDiscountPercent: currentCycle === 'monthly' && !cycleLocked ? yearlyDiscountPercent : null,
      checks,
      onToggleCheck: (index: number) => setChecks(
        (current) => current.map((value, at) => (at === index ? !value : value)),
      ),
      onPickDowngrade: (key: string) => switchToPlan(key, currentCycle),
      onPickYearly: () => switchToPlan(currentKey, 'yearly'),
    },
    review: {
      kind,
      options,
      currentKey,
      currentCycle,
      targetKey: target,
      cycle,
      yearlyDiscountPercent,
      onTargetChange: setTarget,
      onCycleChange: setCycle,
      cycleLocked,
      seats,
      proRataCents: facts.proRataCents,
      daysRemaining: facts.daysRemaining,
      renewalDate,
      previewLoading,
      previewFailed: facts.failed,
      trialing,
      previewRefusal,
      nextChargeCents: facts.nextChargeCents,
      seatReductionCleared: facts.seatReductionCleared,
      onRetryPreview: () => {
        if (seatAware) {
          const lines = changeLines();
          if (lines) void fetchSeatPreview(lines);
          return;
        }
        const request = requestFor();
        if (request) void fetchPreview(request);
      },
      failed: changeFailed || seatChange.isError,
      card,
      cardUpdated,
      onChangeCard: () => setStep('card'),
      declined: null,
    },
  });

  return (
    <PlanFlowShell
      opened={opened}
      eyebrow={t(copy.eyebrow)}
      title={t(copy.title, { plan: targetOption?.title ?? '' })}
      narrow={view === 'card'}
      locked={busy || cardSession.formState === 'submitting'}
      onClose={onClose}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('planFlow.secondary.cancel')}
          </Button>
          <Button
            data-testid="plan-flow-confirm"
            onClick={handleConfirm}
            disabled={isConfirmDisabled({
              mode: view === 'card' ? 'card' : activeMode,
              kind,
              trialing,
              previewRefused: previewRefusal !== null,
              // Active keeps its gate: only the plan preview's own state.
              previewFailed: trialing ? facts.failed : planPreviewFailed,
              previewPending: previewLoading || awaitingTrialFigure,
              cancelChecks: checks,
              cardReady: Boolean(cardSession.formUrl),
              cardBusy: cardSession.formState === 'submitting',
              busy,
            })}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <PlanFlowBody {...body} />
    </PlanFlowShell>
  );
}

/**
 * Narrow the three prop sets down to the one the view actually renders.
 *
 * @param all - The view, and the props each body would need.
 * @returns The body props for the current view.
 */
function bodyFor(all: {
  view: PlanFlowView;
  card: Extract<PlanFlowBodyProps, { view: 'card' }>['card'];
  cancel: Extract<PlanFlowBodyProps, { view: 'cancel' }>['cancel'];
  review: Extract<PlanFlowBodyProps, { view: 'review' }>['review'];
}): PlanFlowBodyProps {
  if (all.view === 'card') return { view: 'card', card: all.card };
  if (all.view === 'cancel') return { view: 'cancel', cancel: all.cancel };
  return { view: 'review', review: all.review };
}

export default PlanFlowModal;
